import { useCallback, useEffect, useRef } from 'react';

/**
 * "Point at it and press Delete" — the mouse half of deleting a chip.
 *
 * A chip already answered the Delete key, but only while it held KEYBOARD focus.
 * A mouse user cannot give it focus without clicking it, and a click opens the
 * editor (that is the chip's primary action and it stays), so for mouse users the
 * gesture simply did not exist. Now the chip the pointer rests on answers Delete
 * too — no selection state to invent, no second meaning for a click.
 *
 * Nothing is written by the key: it asks for the same confirmation a right-click
 * or a focused-chip Delete opens. The guards below are what keep a key typed
 * somewhere ELSE from asking about a send the pointer merely happens to rest on.
 */

/** Elements where Delete / Backspace edit text rather than mean "remove". */
const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]';
/** Surfaces that own the keyboard while they are open — the confirmation itself, the editor, a menu. */
const MODAL = '[role="dialog"], [role="alertdialog"], [role="menu"], [aria-modal="true"]';

export function pointedDeleteApplies(e: KeyboardEvent, target: EventTarget | null): boolean {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return false;
    // Already handled — a chip with keyboard focus deletes ITSELF, not the pointed one.
    if (e.defaultPrevented) return false;
    if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return false;
    const el = target as (Element & { isContentEditable?: boolean }) | null;
    if (el && typeof el.closest === 'function') {
        if (el.isContentEditable || el.closest(EDITABLE)) return false;
        if (el.closest(MODAL)) return false;
    }
    return true;
}

/**
 * Listen for Delete on the chip's document while the pointer is over it.
 * `start` on pointer-enter, `stop` on pointer-leave. `onKey` null = this chip
 * cannot be deleted, and then no key is swallowed.
 *
 * One listener at a time per chip, and only while pointed at: the grid can hold
 * hundreds of chips, and a document listener each would be hundreds of handlers
 * on every keystroke.
 */
export function usePointedDelete(onKey: ((el: HTMLElement) => void) | null) {
    const onKeyRef = useRef(onKey);
    onKeyRef.current = onKey;
    const cleanupRef = useRef<(() => void) | null>(null);

    const stop = useCallback(() => {
        cleanupRef.current?.();
        cleanupRef.current = null;
    }, []);

    const start = useCallback((el: HTMLElement) => {
        stop();
        const doc = el.ownerDocument;
        const listener = (e: KeyboardEvent) => {
            const fire = onKeyRef.current;
            if (!fire || !el.isConnected) return;
            // Inside the extension the grid lives in a shadow root: `target` is
            // retargeted to the host there, so read where the key really landed.
            const target = (typeof e.composedPath === 'function' ? e.composedPath()[0] : null) ?? e.target;
            if (!pointedDeleteApplies(e, target)) return;
            e.preventDefault();
            fire(el);
        };
        doc.addEventListener('keydown', listener);
        cleanupRef.current = () => doc.removeEventListener('keydown', listener);
    }, [stop]);

    useEffect(() => stop, [stop]);

    return { start, stop };
}
